/**
 * PetsAdminDashboard - Admin panel for managing pet listings and providers
 * Features: Moderation queue, provider verification, statistics
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, Eye, Flag,
  Users, ShoppingBag, Calendar, TrendingUp, Loader2,
  FileText, Image, MapPin, Clock, ChevronRight, Search,
  Award, Star, Building, Phone, Mail, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card Component
function StatCard({ icon: Icon, title, value, subtitle, trend, color }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {trend && (
          <div className="mt-2 flex items-center text-xs text-green-600">
            <TrendingUp className="w-3 h-3 mr-1" />
            {trend}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Listing Card for Moderation
function ListingModerationCard({ listing, onModerate }) {
  const [showDetails, setShowDetails] = useState(false);
  
  const statusColors = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    FLAGGED: 'bg-red-100 text-red-800',
    PUBLISHED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-gray-100 text-gray-800',
  };

  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex gap-4">
            {listing.media_urls?.[0] ? (
              <img 
                src={listing.media_urls[0]} 
                alt={listing.title}
                className="w-24 h-24 object-cover rounded-lg"
              />
            ) : (
              <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                <Image className="w-8 h-8 text-gray-400" />
              </div>
            )}
            
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{listing.title}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2">{listing.description}</p>
                </div>
                <Badge className={statusColors[listing.moderation?.status] || 'bg-gray-100'}>
                  {listing.moderation?.status || 'UNKNOWN'}
                </Badge>
              </div>
              
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {listing.location?.city || 'Unknown'}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(listing.created_at).toLocaleDateString()}
                </span>
                {listing.price && (
                  <span className="font-medium text-gray-700">
                    {listing.currency === 'LKR' ? 'Rs.' : '£'}{listing.price}
                  </span>
                )}
              </div>
              
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setShowDetails(true)}>
                  <Eye className="w-3 h-3 mr-1" />
                  View
                </Button>
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => onModerate(listing.listing_id, 'approve')}
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Approve
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="text-amber-600 border-amber-300"
                  onClick={() => onModerate(listing.listing_id, 'warn')}
                >
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Warn
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="text-red-600 border-red-300"
                  onClick={() => onModerate(listing.listing_id, 'reject')}
                >
                  <XCircle className="w-3 h-3 mr-1" />
                  Reject
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Listing Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {listing.media_urls?.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {listing.media_urls.map((url, idx) => (
                  <img key={idx} src={url} alt="" className="h-32 rounded-lg" />
                ))}
              </div>
            )}
            <div>
              <h3 className="font-bold text-lg">{listing.title}</h3>
              <p className="text-gray-600">{listing.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-gray-500">Category</Label>
                <p className="font-medium capitalize">{listing.category}</p>
              </div>
              <div>
                <Label className="text-gray-500">Listing Type</Label>
                <p className="font-medium capitalize">{listing.listing_type}</p>
              </div>
              <div>
                <Label className="text-gray-500">Price</Label>
                <p className="font-medium">{listing.price ? `${listing.currency === 'LKR' ? 'Rs.' : '£'}${listing.price}` : 'Not specified'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Location</Label>
                <p className="font-medium">{listing.location?.city}, {listing.location?.postcode}</p>
              </div>
            </div>
            {listing.pet_details && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-gray-500">Pet Details</Label>
                <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                  {Object.entries(listing.pet_details).map(([key, value]) => (
                    <div key={key}>
                      <span className="text-gray-500 capitalize">{key.replace('_', ' ')}:</span>
                      <span className="ml-1 font-medium">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Provider Verification Card
function VerificationCard({ verification, onReview }) {
  const [showDocs, setShowDocs] = useState(false);
  
  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold">{verification.provider?.display_name || 'Unknown Provider'}</h3>
              <p className="text-sm text-gray-500 capitalize">
                {verification.provider?.provider_type?.replace('_', ' ') || 'Unknown Type'} • {verification.provider?.city || 'Unknown City'}
              </p>
            </div>
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
              Pending Review
            </Badge>
          </div>
          
          <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {verification.documents?.length || 0} documents
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Submitted {new Date(verification.submitted_at).toLocaleDateString()}
            </span>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button size="sm" variant="outline" onClick={() => setShowDocs(true)}>
              <Eye className="w-3 h-3 mr-1" />
              View Documents
            </Button>
            <Button 
              size="sm"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onReview(verification.verification_id, 'approve', 2)}
            >
              <Award className="w-3 h-3 mr-1" />
              Approve (Pro)
            </Button>
            <Button 
              size="sm"
              variant="outline"
              className="text-red-600 border-red-300"
              onClick={() => onReview(verification.verification_id, 'reject')}
            >
              <XCircle className="w-3 h-3 mr-1" />
              Reject
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Documents Dialog */}
      <Dialog open={showDocs} onOpenChange={setShowDocs}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Verification Documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {verification.documents?.map((doc, idx) => (
              <div key={idx} className="p-3 border rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium capitalize">{doc.document_type?.replace('_', ' ')}</p>
                    <p className="text-xs text-gray-500">{doc.document_name || 'Document'}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <a href={doc.document_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    View
                  </a>
                </Button>
              </div>
            ))}
            {verification.notes && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-gray-500">Provider Notes</Label>
                <p className="text-sm mt-1">{verification.notes}</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Select onValueChange={(level) => onReview(verification.verification_id, 'approve', parseInt(level))}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Approve with level..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Level 1 - ID Verified</SelectItem>
                <SelectItem value="2">Level 2 - Professional</SelectItem>
                <SelectItem value="3">Level 3 - Premium</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="text-red-600" onClick={() => onReview(verification.verification_id, 'reject')}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Main Admin Dashboard Component
export default function PetsAdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pendingListings, setPendingListings] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [filterStatus, setFilterStatus] = useState('PENDING');
  
  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, listingsRes, verificationsRes] = await Promise.all([
        fetch(`${API_URL}/api/pets/admin/stats`),
        fetch(`${API_URL}/api/pets/admin/pending-listings?status=${filterStatus}`),
        fetch(`${API_URL}/api/pets/admin/pending-verifications`),
      ]);
      
      const statsData = await statsRes.json();
      const listingsData = await listingsRes.json();
      const verificationsData = await verificationsRes.json();
      
      if (statsData.success) setStats(statsData.stats);
      if (listingsData.success) setPendingListings(listingsData.listings);
      if (verificationsData.success) setPendingVerifications(verificationsData.verifications);
      
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  // Moderate listing
  const handleModerate = async (listingId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/pets/admin/moderate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: listingId,
          action: action,
          reason: action === 'reject' ? 'Does not meet guidelines' : null,
          warning_message: action === 'warn' ? 'Please update listing details' : null,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Listing ${action}ed successfully`);
        setPendingListings(prev => prev.filter(l => l.listing_id !== listingId));
      } else {
        toast.error(data.message || 'Action failed');
      }
    } catch (error) {
      console.error('Moderation error:', error);
      toast.error('Failed to moderate listing');
    }
  };
  
  // Review verification
  const handleReview = async (verificationId, action, level = null) => {
    try {
      const response = await fetch(`${API_URL}/api/pets/admin/review-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_id: verificationId,
          action: action,
          level: level,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Verification ${action}d successfully`);
        setPendingVerifications(prev => prev.filter(v => v.verification_id !== verificationId));
      } else {
        toast.error(data.message || 'Action failed');
      }
    } catch (error) {
      console.error('Review error:', error);
      toast.error('Failed to review verification');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Pets Admin Dashboard
          </h1>
          <p className="text-indigo-200">Manage listings, providers, and verifications</p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            icon={ShoppingBag}
            title="Total Listings"
            value={stats?.listings?.total || 0}
            subtitle={`${stats?.listings?.pending_moderation || 0} pending`}
            color="bg-blue-500"
          />
          <StatCard 
            icon={Users}
            title="Providers"
            value={stats?.providers?.total || 0}
            subtitle={`${stats?.providers?.verified || 0} verified`}
            color="bg-green-500"
          />
          <StatCard 
            icon={AlertTriangle}
            title="Flagged"
            value={stats?.listings?.flagged || 0}
            color="bg-amber-500"
          />
          <StatCard 
            icon={Calendar}
            title="Bookings"
            value={stats?.bookings?.total || 0}
            subtitle={`${stats?.bookings?.pending || 0} pending`}
            color="bg-purple-500"
          />
        </div>
        
        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white shadow-sm mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="moderation" className="flex items-center gap-2">
              <Flag className="w-4 h-4" />
              Moderation
              {pendingListings.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 justify-center text-xs">
                  {pendingListings.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="verification" className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              Verification
              {pendingVerifications.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center text-xs">
                  {pendingVerifications.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Providers by Country</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="flex items-center gap-2">
                        🇬🇧 United Kingdom
                      </span>
                      <span className="font-bold">{stats?.providers?.by_country?.UK || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="flex items-center gap-2">
                        🇱🇰 Sri Lanka
                      </span>
                      <span className="font-bold">{stats?.providers?.by_country?.LK || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" onClick={() => setActiveTab('moderation')}>
                    <Flag className="w-4 h-4 mr-2" />
                    Review Pending Listings ({pendingListings.length})
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setActiveTab('verification')}>
                    <Award className="w-4 h-4 mr-2" />
                    Review Verifications ({pendingVerifications.length})
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={fetchData}>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Refresh Data
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Moderation Tab */}
          <TabsContent value="moderation">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Pending Moderation</h2>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="FLAGGED">Flagged</SelectItem>
                    <SelectItem value="PUBLISHED">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {pendingListings.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">All Clear!</h3>
                    <p className="text-gray-500">No listings pending moderation</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {pendingListings.map(listing => (
                    <ListingModerationCard 
                      key={listing.listing_id}
                      listing={listing}
                      onModerate={handleModerate}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          
          {/* Verification Tab */}
          <TabsContent value="verification">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Pending Verifications</h2>
              
              {pendingVerifications.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Award className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Pending Verifications</h3>
                    <p className="text-gray-500">All verification requests have been processed</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {pendingVerifications.map(verification => (
                    <VerificationCard 
                      key={verification.verification_id}
                      verification={verification}
                      onReview={handleReview}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
